// src/pages/Homepage.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as Dialog from '@radix-ui/react-dialog';
import { Eye, Target, HeartHandshake, Waypoints, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';
import atoImg from '../assets/images/ato-aikins-y842V6cRTKM-unsplash.jpg';
import billImg from '../assets/images/bill-wegener-8ldqRkOk5oo-unsplash.jpg';
import fatimeImg from '../assets/images/pexels-fatima-yusuf-323522203-13988500.jpg';
import kwakuImg from '../assets/images/pexels-kwakugriffn-14554003.jpg'

// Animation variants
const fadeInUp = {
  initial: { y: 60, opacity: 0 },
  animate: { y: 0, opacity: 1 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const AnimatedSection = ({ children, className = '' }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial="initial"
      animate={inView ? 'animate' : 'initial'}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Homepage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

useEffect(() => {
  const interval = setInterval(() => {
    setCurrentSlide(prev => (prev + 1) % 4);
  }, 10000);
  return () => clearInterval(interval);
}, []);


  return (
    <div className="space-y-16 lg:space-y-24 2xl:space-y-32">
      {/* Hero Section */}
     {/* HERO CAROUSEL - Replace your entire <section className="relative min-h-screen..."> */}
<section className="relative min-h-screen flex items-center justify-center overflow-hidden">
  {/* Carousel Images */}
  <div className="absolute inset-0">
    {[
      { img: atoImg, title: "Empowering Futures Through Education", desc: "Providing educational funding opportunities for deserving students to achieve their academic dreams" },
      { img: billImg, title: "Education is the Key", desc: "The Senior High School level is a major determining stage for every child's future development in the Ghanaian education system. Your donation counts in moving young people forward as they grow into adults." },
      { img: fatimeImg, title: "Creating a Future for Our Youth through Education", desc: "Your support for education at the Senior High School level can change the lives of many young people in the Region and beyond. Thank you in advance for your contribution." },
      { img: kwakuImg, title: "Support Youth Education", desc: "Be a part of this cause by contributing financially to support brilliant but needy students in our Senior High Schools. Your donation counts." }
    ].map((slide, index) => (
      <div
        key={index}
        className={`absolute inset-0 transition-opacity duration-1000 ${
          index === currentSlide ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <img
          src={slide.img}
          alt={slide.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>
    ))}
  </div>

  {/* Text Content - EXACTLY like your original */}
  <div className="relative z-10 text-center max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
    <motion.h1
      key={currentSlide + 'title'}
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="text-4xl xs:text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 lg:mb-8 leading-tight text-white"
    >
      {[
        "Empowering Futures Through Education",
        "Education is the Key",
        "Creating a Future for Our Youth through Education",
        "Support Youth Education"
      ][currentSlide]}
    </motion.h1>

    <motion.p
      key={currentSlide + 'desc'}
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="text-lg sm:text-xl lg:text-2xl xl:text-3xl mb-6 lg:mb-12 text-blue-100 max-w-3xl xl:max-w-4xl mx-auto leading-relaxed"
    >
      {[
        "Providing educational funding opportunities for deserving students to achieve their academic dreams",
        "The Senior High School level is a major determining stage for every child's future development in the Ghanaian education system. Your donation counts in moving young people forward as they grow into adults.",
        "Your support for education at the Senior High School level can change the lives of many young people in the Region and beyond. Thank you in advance for your contribution.",
        "Be a part of this cause by contributing financially to support brilliant but needy students in our Senior High Schools. Your donation counts."
      ][currentSlide]}
    </motion.p>

    {/* Donate Button - Unchanged */}
    <div className="hidden lg:block">
      <Dialog.Root>
        <Dialog.Trigger asChild>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary bg-white text-blue-700 hover:text-white"
          >
            Donate
          </motion.button>
        </Dialog.Trigger>
        {/* Your Dialog Portal stays 100% the same */}
        <Dialog.Portal>{/* ... your existing dialog ... */}</Dialog.Portal>
      </Dialog.Root>
    </div>
  </div>

  {/* Left Arrow */}
  <button
    onClick={() => setCurrentSlide(prev => prev === 0 ? 3 : prev - 1)}
    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition"
  >
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  </button>

  {/* Right Arrow */}
  <button
    onClick={() => setCurrentSlide(prev => prev === 3 ? 0 : prev + 1)}
    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition"
  >
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>

  {/* Dots */}
  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
    {[0, 1, 2, 3].map((i) => (
      <button
        key={i}
        onClick={() => setCurrentSlide(i)}
        className={`w-3 h-3 rounded-full transition ${
          currentSlide === i ? 'bg-white w-10' : 'bg-white/60'
        }`}
      />
    ))}
  </div>

  {/* Scroll Indicator - Unchanged */}
  <motion.div
    animate={{ y: [0, -20, 0] }}
    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
    className="absolute bottom-6 sm:bottom-8 lg:bottom-12 left-1/2 transform -translate-x-1/2"
  >
    <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white rounded-full flex justify-center">
      <motion.div
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        className="w-1 h-3 sm:h-4 bg-white rounded-full mt-2"
      />
    </div>
  </motion.div>
</section>

      {/* Who We Are */}
      <AnimatedSection className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-12 lg:mb-20">
          <h2 className="text-3xl xs:text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-4 lg:mb-8">
            Who We Are
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl 2xl:max-w-5xl mx-auto leading-relaxed">
            The M. A. Seidu Education Fund preserves the memory and honours the legacy of the late Mr. Mumuni Abudu Seidu, popularly known as M. A. He was the headmaster of the now Wa Senior High School, Proprietor of Dan-Ibu International School, and the first Regional Director of Education of the Upper West Region. M. A. also served three times as the Member of Parliament for the Wa Central Constituency and was Majority Whip and Minister of State at the Presidency of the Republic of Ghana during his time in Parliament.
          </p>
        </motion.div>
      </AnimatedSection>

      {/* What We Do */}
      <AnimatedSection className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-12 lg:mb-20">
          <h2 className="text-3xl xs:text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-8 lg:mb-12">
            What We Do
          </h2>
           <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl 2xl:max-w-5xl mx-auto leading-relaxed">
            The M. A. Seidu Education Fund aims to provide financial support and mentorship to brilliant but needy students in the Upper West Region of Ghana to enter and complete senior high school successfully. In particular, we provide the following services to students to achieve our objectives:
          </p>
           <h2 className="text-2xl xs:text-2xl sm:text-5xl lg:text-4xl font-bold text-gray-800 mb-8 lg:mb-12">
            THE M. A. SEIDU SCHOLARSHIP PROGRAM
          </h2>
           <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl 2xl:max-w-5xl mx-auto leading-relaxed">
            A scholarship has been set up to provide funding to qualified students. Beneficiaries are selected on a competitive basis by a committee established by the Board of Directors of the Fund. Performance and need are the two main selection criteria. Awards can be used in any government approved senior high school in Ghana.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            {[
              {
                title: 'Awards',
                description: 'Awards will range from 2000 to 5000 Cedis, depending on need and availability of funds. The money will help beneficiaries cover the cost of the prospectus for the first year of senior high school.',
                icon: '🏆'
              },
              {
                title: 'Mentorship',
                description: 'The M. A. Seidu Education Fund also provides guidance to beneficiaries throughout the three years in senior high school in areas related to their education, career and social development.',
                icon: '🧑‍🏫'
              },
              {
                title: 'Target Population',
                description: 'The scholarship is awarded to residents of the Upper West Region who attend one of the junior high schools in the region.',
                icon: '🎯'
              },
              {
                title: 'FUNDING TARGET',
                description: 'The Fund aims to raise cash and in-kind contributions to support a minimum of 20 students every academic year. We will rely on private and corporate donations to meet its objectives and financial obligations.',
                icon: '🎯💰'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -10 }}
                className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-4 lg:mb-6 text-blue-600">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-start text-base sm:text-lg lg:text-xl leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </AnimatedSection>

    {/* Vision, Mission, Core Values, Priority & Focus Area */}
<AnimatedSection className="bg-gray-50 py-16 lg:py-24 2xl:py-32">
  <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-12 xl:gap-16">

      {/* Our Vision */}
      <motion.div
        variants={fadeInUp}
        className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
      >
        <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Eye className="w-10 h-10 text-blue-600" />
        </div>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
          Our Vision
        </h3>
        <p className="text-gray-600 text-base sm:text-lg lg:text-xl leading-relaxed">
          Enhance educational opportunities for young people in the Upper West Region by removing financial barriers.
        </p>
      </motion.div>

      {/* Our Mission */}
      <motion.div
        variants={fadeInUp}
        className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
      >
        <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Target className="w-12 h-12 text-green-600" />
        </div>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
          Our Mission
        </h3>
        <p className="text-gray-600 text-base sm:text-lg lg:text-xl leading-relaxed">
          Provide financial support to brilliant but needy students.
        </p>
      </motion.div>

      {/* Core Values */}
      <motion.div
        variants={fadeInUp}
        className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
      >
        <div className="bg-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <HeartHandshake className="w-12 h-12 text-purple-600" />
        </div>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-6">
          Core Values
        </h3>
        <div className="flex flex-wrap justify-center gap-3">
          {['Knowledge', 'Innovation', 'Achievement'].map((value) => (
            <span
              key={value}
              className="font-semibold text-purple-700 bg-purple-50 px-5 py-2.5 rounded-full text-sm sm:text-base border border-purple-200"
            >
              {value}
            </span>
          ))}
        </div>
      </motion.div>

      {/* Priority Area */}
      <motion.div
        variants={fadeInUp}
        className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 md:col-span-2 xl:col-span-1"
      >
        <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
         <Waypoints className="w-12 h-12 text-orange-600" />
        </div>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-6">
          Priority Area
        </h3>
        <p className="text-gray-600 text-base sm:text-lg lg:text-xl leading-relaxed font-medium">
          Secondary education — bridging the gap between junior and senior high school.
        </p>
      </motion.div>

      {/* Focus Area */}
      <motion.div
        variants={fadeInUp}
        className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 md:col-span-2 xl:col-span-1"
      >
        <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <MapPin className="w-12 h-12 text-teal-600" />
        </div>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-6">
          Focus Area
        </h3>
        <p className="text-gray-600 text-base sm:text-lg lg:text-xl leading-relaxed font-medium">
          All districts in the Upper West Region of Ghana.
        </p>
      </motion.div>

    </div>
  </div>
</AnimatedSection>

      {/* Roles and Responsibilities */}
      <AnimatedSection className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-12 lg:mb-20">
          <h2 className="text-3xl xs:text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-8 lg:mb-12">
            Roles & Responsibilities
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
            {/* Students & Parents */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100"
            >
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">👨‍🎓</span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-6 lg:mb-8 text-blue-600">
                Students & Parents
              </h3>
              <ul className="text-left space-y-3 lg:space-y-4 text-gray-600 text-base sm:text-lg lg:text-xl">
                {[
                  'Sign a contract with Fund Managers to receive financial support from the Fund',
                  'Consult regularly with Fund managers to ensure progress and success of student beneficiaries',
                  'Report progress and challenges regularly to Fund managers',
                  'Share knowledge and experiences, and lessons learned with Fund managers to help build capacity',
                  'Help identify potential donors and activities for investment'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-3 lg:mr-4 mt-1 flex-shrink-0 text-xl">✓</span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Fund Managers */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-100"
            >
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">👨‍💼</span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-6 lg:mb-8 text-green-600">
                Fund Managers
              </h3>
              <ul className="text-left space-y-3 lg:space-y-4 text-gray-600 text-base sm:text-lg lg:text-xl">
                {[
                  'Provide guidance to student beneficiaries and their guardians on how to access the Fund and maximize its benefits.',
                  'Work with the Board of Directors to identify and explore funding sources and potential donors',
                  'Organize and support funding campaigns and fundraisers',
                  'Seek partnership opportunities with donor organizations, including corporate Ghana and Government to enhance contributions to the Fund',
                  ' Provide mentoring and training opportunities to student beneficiaries',
                  'Share lessons learned and success stories with Fund and donors',
                  ' Prepare financial accounts, administrative reports and other Fund documentation, organize and store all Fund-related material to support the operation of the Fund',
                  'Suggest improvements to the Fund for consideration by the Board of Directors, Donors and Partners'
                ].map((responsibility, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-3 lg:mr-4 mt-1 flex-shrink-0 text-xl">✓</span>
                    <span>{responsibility}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </AnimatedSection>
    </div>
  );
};

export default Homepage;